import type { ZodArray, ZodBoolean } from 'zod';
import { z } from 'zod';

import type { SortedField } from '~/shared/data-contract/common';
import { baseJobSchema } from '~/shared/data-contract/job';
import { baseLearningOpportunitySchema } from '~/shared/data-contract/learning-opportunities';
import { paginationSchema } from '~/shared/data-contract/pagination';
import { baseSkillSchema, skillShortSchema } from '~/shared/data-contract/skill';
import { baseUserSchema, extendedUserSchema } from '~/shared/data-contract/user';

const createGlobalSearchSectionSchema = <ItemType extends z.ZodTypeAny>(
    itemSchema: ItemType,
): z.ZodObject<{ readonly data: ZodArray<ItemType>; readonly hasMore: ZodBoolean }> =>
    z.object({
        data: itemSchema.array(),
        hasMore: z.boolean(),
    });

const globalSearchSectionSkillsSchema = z.object({
    data: baseSkillSchema.array(),
    failed: z.boolean().default(false),
    hasMore: z.boolean(),
});

export const globalSearchSchema = z.object({
    jobs: createGlobalSearchSectionSchema(baseJobSchema),
    learningOpportunities: createGlobalSearchSectionSchema(baseLearningOpportunitySchema),
    searchTerm: z.string(),
    skills: globalSearchSectionSkillsSchema,
    users: createGlobalSearchSectionSchema(baseUserSchema),
});

export type GlobalSearch = z.infer<typeof globalSearchSchema>;

const baseGlobalJobSchema = z.object({
    id: z.string(),
    publishedAt: z.string().nullable().optional(),
    title: z.string(),
});

const baseGlobalSearchSkillSchema = z.object({
    jobsWithThisSkill: z.number(),
    learningOpportunitiesWithThisSkill: z.number(),
    skillGap: z.object({
        absolute: z.number(),
        relative: z.number(),
    }),
    usersWithThisSkill: z.number(),
});

export const globalSearchSkillSchema = baseGlobalSearchSkillSchema.and(baseSkillSchema);

export type GlobalSearchSkill = z.infer<typeof globalSearchSkillSchema>;

export const globalSearchSkillResponseSchema = z.object({
    data: globalSearchSkillSchema.array(),
    pagination: paginationSchema,
});

export type GlobalSearchSkillResponse = z.infer<typeof globalSearchSkillResponseSchema>;

export const globalOptionsSchema = z.object({
    canDirectoryJobs: z.boolean(),
    canDirectoryLearningOpportunities: z.boolean(),
    canDirectorySkills: z.boolean(),
    canDirectoryUsers: z.boolean(),
    canGetCompanySetting: z.boolean().default(false),
    canSearchJobs: z.boolean(),
    canSearchLearningOpportunities: z.boolean(),
    canSearchSkills: z.boolean(),
    canSearchUsers: z.boolean(),
    canUpdateCompanySetting: z.boolean().default(false),
});

export type GlobalOptions = z.infer<typeof globalOptionsSchema>;

export const globalPermissionsSchema = globalOptionsSchema.extend({
    canUserSearch: z.boolean(),
    canUserTalentDirectory: z.boolean(),
});

export type GlobalPermissions = z.infer<typeof globalPermissionsSchema>;

const globalSearchUserSchema = extendedUserSchema.extend({
    skills: skillShortSchema.array(),
});

export const globalSearchUserResponseSchema = z.object({
    data: globalSearchUserSchema.array(),
    pagination: paginationSchema,
});

export type GlobalSearchUserResponse = z.infer<typeof globalSearchUserResponseSchema>;

export type GlobalSearchUser = z.infer<typeof globalSearchUserSchema>;

const globalSearchJobSchema = baseGlobalJobSchema.and(
    z.object({
        skills: skillShortSchema.array(),
    }),
);

export type GlobalSearchJob = z.infer<typeof globalSearchJobSchema>;

export const globalSearchJobResponseSchema = z.object({
    data: globalSearchJobSchema.array(),
    pagination: paginationSchema,
});

export type GlobalSearchJobResponse = z.infer<typeof globalSearchJobResponseSchema>;

export type GlobalSearchJobSkill = GlobalSearchJob['skills'][number];

export const globalSearchSkillUsedResponseSchema = z.object({
    data: globalSearchSkillSchema.array(),
    pagination: paginationSchema,
});

export const globalSearchRelatedSkillsResponseSchema = z.object({
    relatedSkillData: globalSearchSkillUsedResponseSchema,
    skillData: globalSearchSkillSchema.array(),
});

export type GlobalSearchRelatedSkillsResponse = z.infer<typeof globalSearchRelatedSkillsResponseSchema>;

export type GlobalSearchPagination = {
    readonly page: number;
    readonly size: number;
};

export type GlobalSearchParams = {
    readonly pagination: GlobalSearchPagination;
    readonly searchTerm: string;
    readonly sortedFields?: readonly SortedField[];
};

export type GlobalSearchBySkillIdsParams = {
    readonly pagination: GlobalSearchPagination;
    readonly skillIds: ReadonlyArray<string>;
    readonly sortedFields?: readonly SortedField[];
};

const globalSearchLearningOpportunitySchema = z.object({
    id: z.string(),
    createdAt: z.string().optional(),
    skills: skillShortSchema.array(),
    title: z.string(),
    type: z.string(),
});

export type GlobalSearchLearningOpportunity = z.infer<typeof globalSearchLearningOpportunitySchema>;

export const globalSearchLearningOpportunityResponseSchema = z.object({
    data: globalSearchLearningOpportunitySchema.array(),
    pagination: paginationSchema,
});

export type GlobalSearchLearningOpportunityResponse = z.infer<typeof globalSearchLearningOpportunityResponseSchema>;

export const globalSearchTermSchema = z.string().trim().min(1);

export const globalSearchJobProfileSchema = z.object({
    id: z.uuid(),
    title: z.string(),
});

export type GlobalSearchJobProfile = z.infer<typeof globalSearchJobProfileSchema>;

export const globalSearchJobProfileResponseSchema = z.object({
    data: globalSearchJobProfileSchema.array(),
    pagination: paginationSchema,
});

export type GlobalSearchJobProfileResponse = z.infer<typeof globalSearchJobProfileResponseSchema>;

const jobArchitectureHierarchySchema = z.object({
    clusterId: z.string().nullable().optional(),
    clusterTitle: z.string().nullable().optional(),
    familyId: z.string().nullable().optional(),
    familyTitle: z.string().nullable().optional(),
    jobArchitectureId: z.string(),
    jobArchitectureTitle: z.string(),
});

export const globalSearchJobRoleSchema = jobArchitectureHierarchySchema.extend({
    id: z.string(),
    title: z.string(),
});

export type GlobalSearchJobRole = z.infer<typeof globalSearchJobRoleSchema>;

export const globalSearchJobRoleResponseSchema = z.object({
    data: globalSearchJobRoleSchema.array(),
    pagination: paginationSchema,
    searchTerm: z.string(),
});

export type GlobalSearchJobRoleResponse = z.infer<typeof globalSearchJobRoleResponseSchema>;

export const globalSearchJobLevelSchema = globalSearchJobRoleSchema.extend({
    roleId: z.string(),
    roleTitle: z.string(),
});

export type GlobalSearchJobLevel = z.infer<typeof globalSearchJobLevelSchema>;

export const globalSearchJobLevelResponseSchema = z.object({
    data: globalSearchJobLevelSchema.array(),
    pagination: paginationSchema,
    searchTerm: z.string(),
});

export type GlobalSearchJobLevelResponse = z.infer<typeof globalSearchJobLevelResponseSchema>;
